package com.siriusxm.pia.views.account

import androidx.compose.runtime.*
import com.siriusxm.pia.Application
import com.siriusxm.pia.SXMUI
import com.siriusxm.pia.components.IconSize
import com.siriusxm.pia.components.border
import com.siriusxm.pia.components.borderTop
import com.siriusxm.pia.components.icon
import kotlinx.browser.window
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import kotlin.time.Duration.Companion.seconds

/**
 * Hover popup panel shown when the user clicks their username in the header.
 * Displays a summary of the user's profile: email, Cognito group badges (if any),
 * and quick links to the full account page and sign out.
 *
 * @param onDismiss Callback to close the popup
 */
@Composable
internal fun UserAccountPopup(onDismiss: () -> Unit) {
    val viewer = Application.viewer

    Div({ classes(PopupStyles.container) }) {
        // Header with username and email
        Div({ classes(PopupStyles.header) }) {
            Text(viewer.email?.substringBefore("@") ?: "User")
            Div({ classes(PopupStyles.subtext) }) {
                Text(viewer.email ?: "")
            }
        }

        // Groups section — only shown if the user belongs to any known groups
        val activeGroups = com.siriusxm.pia.UserGroup.entries.filter { viewer.isInGroup(it) }
        if (activeGroups.isNotEmpty()) {
            Div({ classes(PopupStyles.section) }) {
                Div({ classes(PopupStyles.sectionLabel) }) {
                    Text("Roles")
                }
                UserGroupBadges()
            }
        }

        // Links
        Div({ classes(PopupStyles.divider) }) {
            A(href = "#account", {
                classes(PopupStyles.actionLink)
                onClick {
                    it.preventDefault()
                    Application.navigate("account")
                    onDismiss()
                }
            }) {
                Text("View Account")
            }
            if (viewer.aggregatorAdmin) {
                CopyAccessTokenAction()
            }
            A(href = "#", {
                classes(PopupStyles.actionLink)
                onClick {
                    it.preventDefault()
                    com.siriusxm.unifiedcontent.console.subscriber.SubscriberConnection.logout()
                }
            }) {
                Text("Sign Out")
            }
        }
    }
}

/**
 * Admin-only popup action that copies the current session's raw access token — the value the API
 * clients send after `Bearer ` in the `Authorization` header, without that prefix — to the clipboard,
 * for pasting into curl, Postman, and similar tools.
 *
 * The token is read from [Application.cognito] at click time rather than from [Application.viewer],
 * whose token is captured once and goes stale after a refresh. The label briefly switches to "Copied"
 * as confirmation.
 */
@Composable
private fun CopyAccessTokenAction() {
    var copied by remember { mutableStateOf(false) }

    LaunchedEffect(copied) {
        if (copied) {
            delay(2.seconds)
            copied = false
        }
    }

    A(href = "#", {
        classes(PopupStyles.actionLink, PopupStyles.iconAction)
        title("Copy the current session's access token to the clipboard")
        onClick {
            it.preventDefault()
            val token = Application.cognito.accessToken
            if (!token.isNullOrBlank()) {
                window.navigator.clipboard.writeText(token).then { copied = true }
            }
        }
    }) {
        icon("content_copy") {
            size = IconSize.TINY
        }
        Text(if (copied) "Copied" else "Copy Access Token")
    }
}

/**
 * Stylesheet for the user account popup panel. Contains styles for the popup
 * container, header, sections, and action links.
 */
object PopupStyles : StyleSheet() {
    val container by style {
        minWidth(280.px)
        padding(1.cssRem)
    }

    val header by style {
        fontSize((0.875).cssRem)
        fontWeight(600)
        color(SXMUI.textColor.value())
        paddingBottom((0.5).cssRem)
        border(bottom = 1.px, color = SXMUI.borderDividerDefault.value())
        marginBottom((0.75).cssRem)
    }

    val subtext by style {
        fontSize((0.75).cssRem)
        color(SXMUI.subtleTextColor.value())
        marginTop((0.125).cssRem)
    }

    val section by style {
        marginBottom((0.75).cssRem)
    }

    val sectionLabel by style {
        fontSize((0.6875).cssRem)
        color(SXMUI.subtleTextColor.value())
        marginBottom((0.25).cssRem)
        fontWeight(500)
        property("text-transform", "uppercase")
        property("letter-spacing", "0.5px")
    }

    val divider by style {
        borderTop()
        marginTop((0.75).cssRem)
        paddingTop((0.75).cssRem)
    }

    val actionLink by style {
        display(DisplayStyle.Block)
        fontSize((0.8125).cssRem)
        color(SXMUI.brandColor.value())
        cursor("pointer")
        padding((0.25).cssRem, 0.px)
        textDecoration("none")

        self + hover style {
            textDecoration("underline")
        }
    }

    val iconAction by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap((0.375).cssRem)
    }
}
